import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Box, Button, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactGA from "react-ga4";
import { useAuth } from "@workos-inc/authkit-react";

import Icon from './components/Icon';
import Lockup from './components/Lockup';
import OutlinedCircle from './components/OutlinedCircle';
import ToastBar from './components/ToastBar';
import ViewToggle, {
  TOGGLE_EASE,
  TOGGLE_MOVE_MS,
  type ToggleValue,
  type ViewName,
  type ViewToggleHandle,
} from './components/ViewToggle';
import ViewCrossfade, { type CrossfadeHandle } from './components/ViewCrossfade';
import {
  TOGGLE_BAND,
  TOGGLE_NARROW_SCALE,
  toggleWidth,
  type ToggleItem,
  type TogglePill,
} from './components/viewToggleMetrics';
import AboutModal from './features/about/AboutModal';
import EventDetailSheet, { type CalloutLayout } from './features/explore/EventDetailSheet';
import EventList from './features/explore/EventList';
import ResizableSidebar from './components/ResizableSidebar';
import RightColumn from './components/RightColumn';
import SidebarToggle from './components/SidebarToggle';
import {
  CentreColumn,
  PhoneToggleDock,
  // AccountLinkSpot, (the Account link the view toggle's third item replaced)
  // PreviewLinks, (the retired Log in / Sign up)
  RIGHT_SIDEBAR_X,
  RightSidebar,
  RightSidebarBody,
  SidebarTop,
  StripLinks,
  StripLockup,
  ToggleDock,
  ToggleSpot,
  TopStrip,
} from './components/Frame';
import Sidebar from './features/explore/Sidebar';
import ReportDialog from './features/report/ReportDialog';
import MobileNotice from './features/mobile/MobileNotice';
import Schedule, { type ScheduleHandle } from './features/schedule/Schedule';
import {
  EMPTY_FILTERS,
  activeFilterCount,
  filterKey as signatureOf,
  findLoosenSuggestion,
  listQuery,
  matchesLocally,
  poolQuery,
  relax,
  type FilterState,
  type ListContext,
  type LoosenSuggestion,
} from './features/explore/filters';
import { useOpportunityCount, useOpportunityFeed } from './features/explore/useOpportunityFeed';
import { BRAND } from './shared/theme/paletteDark';
import { FONTS } from './shared/theme/typography';
import { useCollections } from './shared/useCollections';
import { WeekContext, type WeekSummary } from './shared/weekContext';
import { useWeekStats } from './features/schedule/weekStats';
import { quoted, useToasts } from './shared/useToasts';
import { useAccountData } from './shared/account/context';
import { isPastEvent, useNow } from './shared/pastEvents';
import { useHiddenEvents } from './shared/useHiddenEvents';
import EventEditDialog from './features/edit/EventEditDialog';
import {
  applyEdit,
  useEventEdits,
  type EditScope,
  type EventFields,
} from './features/edit/eventEdits';
import { usePersonalEvents } from './features/schedule/usePersonalEvents';
import { useAttendance } from './features/schedule/useAttendance';
import { useLegChoices } from './features/schedule/useLegChoices';
import { useHome } from './features/schedule/useHome';
import DayFocus from './features/explore/DayFocus';
import { useHoverStore } from './features/explore/useHoverPreview';
import { clearScrollMemory, useScrollMemory } from './shared/useScrollMemory';
import { useMobileNotice } from './shared/useIsPhone';
import { useIsMobile } from './shared/useIsMobile';
import { usePhoneChrome } from './shared/usePhoneChrome';
import { useLocation, useNavigate } from 'react-router';
import { ACCOUNT_PATHS, ACCOUNT_ROOT } from './features/account/paths';
import { ACCOUNT_HINT_KEY } from './shared/account/keys';
import { ACCOUNTS_ENABLED } from './features/account/accountsEnabled';
import { arrivalFrom, type ReturnTo } from './features/account/returnTo';
import FilterPanel, { type FilterPanelHandle } from './features/explore/FilterPanel';
import PageSwipe, { type PageSwipeHandle } from './components/PageSwipe';
import { startsOnOwnDrag, useSwipeGesture } from './shared/useSwipeGesture';
import { useListAnchor } from './features/explore/useListAnchor';
import { useCalloutTrail } from './features/explore/useCalloutTrail';
import { useSidebars } from './features/explore/useSidebars';
import { initTelemetry, track } from './shared/telemetry';
import {
  compareOpportunities,
  isOpportunityId,
  localDate,
  type Opportunity,
} from './shared/opportunity';
import { useOpportunityDirectory } from './shared/useOpportunityDirectory';
import { useTaxonomy } from './shared/useTaxonomy';
import type { OtherFilter, TimeSlot } from './shared/taxonomy';
import {
  HIDE_MS,
  byWhen,
  FEEDBACK_URL,
  WORDMARK_W,
  SWIPE_HINT_KEY,
  SWIPE_CONFIRM_MS,
  SWIPE_FADE_MS,
  LOCKUP_IN_SIDEBAR,
  LOCKUP_IN_STRIP,
} from './shared/utils/constants';
import {
  Shell,
  Content,
  Header,
  HeaderRight,
  HeaderToggle,
  Panes,
  Pane,
  Centered,
  PaneWrap,
  Fade,
  TextLink,
  PHONE_HEADER_BELOW,
  ResultsBar,
  SavedPill,
  ShowingLine,
} from './shared/SharedComponents';

/** Search waits for a pause in typing; every other control asks at once. */
const SEARCH_DEBOUNCE_MS = 500;

/** A card on its way out of the list on screen. */
interface Departure {
  /** Which list it is leaving: the main list, or the one under the Saved pill. */
  savedView: boolean;
  /** Its row is closing; until then it holds its place. */
  closing: boolean;
  token: number;
}

export default function App({ children }: { children?: ReactNode }) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  /** The phone layout. Narrower than the narrow one; see `useIsMobile`. */
  const isMobile = useIsMobile();
  /** A phone, as against a narrow window: the bottom toggle and the new chrome. */
  const phoneChrome = usePhoneChrome();
  /**
   * A desktop window too narrow for a full-size pill beside even the smallest
   * lockup: it falls back to the 0.75x Sam mocked rather than the two touching.
   */
  const narrowToggle = useMediaQuery('(max-width: 459.95px)');
  /**
   * Where My Schedule's Share button is drawn on a phone: in the header, from
   * inside Schedule, so that exactly one ShareMenu stays mounted with the
   * state and the login-return effect that belong to it.
   */
  const [shareSlot, setShareSlot] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  // Log out is on the Account page now (D3), which checks for unsaved changes first.
  const { isLoading: authIsLoading, user /* , signOut */ } = useAuth();

  /**
   * Account (D3), on every host: the brake hides it from logged-out visitors
   * only. Read this early because the toggle's width follows from it, and the
   * desktop frame lays the top strip out around that width.
   */
  const accountShown = ACCOUNTS_ENABLED || Boolean(user);
  /**
   * The account this browser last showed. While AuthKit is still restoring the
   * session the pill already holds the icon rather than "Sign In", so it does
   * not change shape a moment after the page appears.
   */
  const [accountHint] = useState(() => {
    try {
      return Boolean(localStorage.getItem(ACCOUNT_HINT_KEY));
    } catch {
      return false;
    }
  });
  const signedIn = Boolean(user) || (authIsLoading && accountHint);
  const togglePill: TogglePill = signedIn ? 'signedIn' : accountShown ? 'signedOut' : 'braked';
  const toggleW = toggleWidth(togglePill);

  /** The user's own corrections, applied over the listings everywhere they appear. */
  const edits = useEventEdits();
  /** Events marked Not Interested. */
  const hidden = useHiddenEvents();
  const collections = useCollections();
  const toasts = useToasts();
  /** Where the lists live (the device or the account), and what it has to say. */
  const account = useAccountData();
  const personal = usePersonalEvents();
  /**
   * How much of each event the user is attending.
   *
   * Held here rather than inside My Schedule because the callout asks the same
   * questions the day column does — is this a clash, can I get there in time —
   * and answering them from a different set of times is how the two came to
   * contradict each other.
   */
  const attendance = useAttendance();
  /**
   * How the user said they are travelling between events, and when they leave.
   * Here for the same reason: the callout draws the same journeys the day does.
   */
  const legChoices = useLegChoices();
  /** Where the user is staying, if they have said: MAP MY DAY starts and ends there. */
  const home = useHome();
  /** Topics, goals and event-type words: `GET /taxonomy`, with local stand-ins. */
  const vocabulary = useTaxonomy();

  /**
   * Set when the user follows "Go to My Schedule". The schedule opens the day,
   * scrolls the event to center, and only then fades the card in — so the
   * arrival lands on the event rather than leaving it to be hunted for.
   */
  const [pendingReveal, setPendingReveal] = useState<{ id: string; dayKey: string } | null>(null);
  /** Set by a click on a mini day: My Schedule opens that day, around the hours the mini day showed. */
  const [pendingDay, setPendingDay] = useState<{ dayKey: string; minute: number } | null>(null);

  // Phones only, once per browser, for the duration of the beta.
  const mobileNotice = useMobileNotice();

  // Both Explore columns keep their scroll position across a trip to My
  // Schedule, which unmounts them.
  const listScroll = useScrollMemory('explore:list');
  // ...and their place in the week across a filter change.
  const listAnchor = useListAnchor(listScroll.node);

  // const [view, setView] = useState<ViewName>('explore');
  // Explore, unless the page was opened on My Schedule: back from logging in
  // from there, or from Share My Schedule (returnTo.ts).
  const location = useLocation();
  const arrival = location.state;
  const [view, setView] = useState<ViewName>(() =>
    arrivalFrom(arrival)?.view === 'schedule' ? 'schedule' : 'explore',
  );
  /** The account page is open over the app; App itself stays mounted (AppShell). */
  const accountOpen =
    location.pathname === ACCOUNT_ROOT || location.pathname.startsWith(`${ACCOUNT_ROOT}/`);
  /**
   * Its index page. The toggle is drawn on `/` and here, and nowhere else under
   * `/account`: login, the callback and the rest are journeys, not views.
   */
  const accountPage = location.pathname === ACCOUNT_ROOT || location.pathname === `${ACCOUNT_ROOT}/`;
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  /** The callout's event, and its history. */
  const {
    detail,
    show: showDetail,
    close: closeDetail,
    update: updateDetail,
    back: detailBack,
    forward: detailForward,
  } = useCalloutTrail();
  /** The callout was opened from a card in the day view: half up, below 900px. */
  const [detailFromDay, setDetailFromDay] = useState(false);
  const [reporting, setReporting] = useState<Opportunity | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  /** Search and Filter, below 900px: the panel behind the tab on the left edge. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  /** The day the list is scrolled to, from 900px up: Explore's right sidebar shows it. */
  const [focusedDay, setFocusedDay] = useState<string | null>(null);
  /** The card under the pointer, which the same sidebar previews. Held outside React's state, so a hover redraws only the sidebar. */
  const hover = useHoverStore();
  /** Explore's two sidebars from 900px up, and the view toggle's place. */
  const {
    calloutFits,
    rightHolds,
    rightOpen,
    filtersShown,
    leftFraction,
    maxLeftFraction,
    filtersEdge,
    toggleX,
    setLeftShare,
    toggleLeft,
    toggleRight,
  } = useSidebars({ isNarrow, callout: detail !== null, onCloseCallout: closeDetail, toggleW });

  /** How the callout is drawn; see `CalloutLayout`. */
  const calloutLayout: CalloutLayout = !isNarrow
    ? calloutFits
      ? 'pane'
      : 'over'
    : detailFromDay
      ? 'half'
      : isMobile
        ? 'sheet'
        : 'over';

  const [editing, setEditing] = useState<Opportunity | null>(null);

  const header = useRef<HTMLElement | null>(null);

  /** Bumped once to demonstrate the swipe on the first card; see where it fires, below. */
  const [swipeHint, setSwipeHint] = useState(0);
  const hinted = useRef(false);

  useEffect(() => initTelemetry(), []);

  // Undo and redo describe themselves through the same strip as everything else.
  useEffect(() => {
    if (collections.announcement) toasts.show(collections.announcement.message);
    // Only when a new announcement arrives, not when `toasts` re-identifies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections.announcement]);

  // So does the move to an account: once when it's done, once if it has to wait.
  useEffect(() => {
    if (!account.notice) return;
    toasts.show(account.notice.message);
    account.dismissNotice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.notice]);

  const markedIds = useMemo(
    () => new Set<string>([...collections.scheduled, ...collections.saved]),
    [collections.scheduled, collections.saved],
  );
  const editedIds = useMemo(() => new Set(Object.keys(edits.edits)), [edits.edits]);

  // --- what Explore asks for -------------------------------------------------

  /**
   * The search as it is sent. The box follows every keystroke; the request
   * waits for a pause in typing, so a word typed is one request rather than one
   * per letter. Emptying the box is not something to wait on.
   */
  const [searched, setSearched] = useState(filters.query);
  useEffect(() => {
    if (searched === filters.query) return;
    const timer = setTimeout(
      () => setSearched(filters.query),
      filters.query.trim() ? SEARCH_DEBOUNCE_MS : 0,
    );
    return () => clearTimeout(timer);
  }, [filters.query, searched]);
  const asked = useMemo<FilterState>(() => ({ ...filters, query: searched }), [filters, searched]);

  /**
   * Bumped by the wordmark, and folded into the signature below.
   *
   * "Start over" has to collapse the list as well as scroll it up, and the list
   * already starts again on a changed signature. Routing it through the same
   * channel is what keeps the two in step.
   */
  const [homeNonce, setHomeNonce] = useState(0);
  /**
   * Bumped when an event should come back that the loaded pages never held:
   * one shown again, or with its edits undone, after the API had been asked to
   * leave it out.
   */
  const [refreshNonce, setRefreshNonce] = useState(0);

  /**
   * A stable signature of the filter criteria. The list starts again from a
   * first page on this rather than on anything else, so saving, hiding or
   * editing an event — which changes what is shown but not what was asked —
   * keeps the pages already loaded and the user's place in them.
   */
  const filterKey = useMemo(
    () => `${signatureOf(asked)}|${homeNonce}|${refreshNonce}`,
    [asked, homeNonce, refreshNonce],
  );

  const context = useMemo<ListContext>(
    () => ({ hiddenIds: hidden.ids, editedIds, markedIds }),
    [hidden.ids, editedIds, markedIds],
  );
  const request = useMemo(() => listQuery(asked, context), [asked, context]);
  const feed = useOpportunityFeed({ key: filterKey, query: request, anchor: listAnchor.current });

  // --- events by id ------------------------------------------------------------

  /** Everything the user's own lists point at, fetched by id and kept current. */
  const directoryIds = useMemo(
    () => [...collections.scheduled, ...collections.saved, ...hidden.ids, ...editedIds],
    [collections.scheduled, collections.saved, hidden.ids, editedIds],
  );
  const directory = useOpportunityDirectory(directoryIds, feed.items);

  /** Every listing this session holds, as the user sees it: their edits applied. */
  const publishedById = useMemo(() => {
    const map = new Map<string, Opportunity>();
    for (const [id, event] of directory.byId) {
      map.set(id, edits.edits[id] ? applyEdit(event, edits.edits[id]) : event);
    }
    return map;
  }, [directory.byId, edits.edits]);

  /**
   * Every event this session can name: the listings plus whatever the user
   * added themselves.
   *
   * Merged here so the machinery of My Schedule — the day layout, travel legs,
   * conflicts, the .ics export — treats a personal event as an event, without
   * a single branch asking which kind it is.
   *
   * Explore never sees this map. It is a directory of Climate Week, not of the
   * user's day: dinner has no tags to filter by, no callout worth opening, and
   * no business in a list of things to discover. Personal events reach the
   * schedule and stop there.
   *
   * They are also always on the schedule: adding one *is* scheduling it, and
   * there is no other list for it to live in.
   */
  const byId = useMemo(
    () => new Map([...publishedById, ...personal.events.map((e) => [e.id, e] as const)]),
    [publishedById, personal.events],
  );

  // --- Explore's list ------------------------------------------------------------

  /** New York's clock, moved on when an event on screen ends. */
  const now = useNow(feed.items);
  const tick = `${now.dayKey}:${now.minutes}`;

  /** Edited listings: left out of every request, and placed by the browser instead. */
  const editedEvents = useMemo(
    () =>
      [...editedIds]
        .map((id) => publishedById.get(id))
        .filter((e): e is Opportunity => Boolean(e)),
    [editedIds, publishedById],
  );

  const editedMatches = useMemo(
    () =>
      editedEvents
        .filter((e) => matchesLocally(e, asked, context, now))
        .sort(compareOpportunities),
    [editedEvents, asked, context, now],
  );

  /**
   * Saved and scheduled events live under the Saved pill only, so an event that
   * changes lists leaves the list on screen: one added from the main list, or,
   * on a phone, one taken off both lists under the pill. Its card stays long
   * enough to play the hide animation, and after a phone swipe first holds its
   * row while the swipe confirms. One taken off both lists comes back to the
   * main list, fetched again if the loaded pages never held it.
   */
  const [departing, setDeparting] = useState<ReadonlyMap<string, Departure>>(() => new Map());
  /**
   * The lists as the list on screen has taken account of them. It moves on in
   * the same update that starts a departure, never a render before it: filtering
   * on the lists themselves would drop a leaving card for one render, and it
   * would come back as a new card with its animation lost.
   */
  const [listedMarked, setListedMarked] = useState(markedIds);
  /**
   * Swiped on a phone since the lists last changed, and when. The hold is timed
   * from the swipe rather than from this effect, which runs a render later, so
   * the row closes just as the card's confirmation finishes fading.
   */
  const swiped = useRef(new Map<string, number>());
  const departures = useRef(0);
  const previousMarked = useRef(markedIds);
  useEffect(() => {
    const before = previousMarked.current;
    previousMarked.current = markedIds;
    const added = [...markedIds].filter((id) => !before.has(id));
    const removed = [...before].filter((id) => !markedIds.has(id));
    const savedView = asked.savedOnly;
    const leavingNow = (savedView ? (isMobile ? removed : []) : added).filter((id) =>
      feed.items.some((e) => e.id === id),
    );

    // A later departure of the same event owns it from then on.
    const update = (id: string, token: number, next: Departure | null) =>
      setDeparting((current) => {
        if (current.get(id)?.token !== token) return current;
        const map = new Map(current);
        if (next) map.set(id, next);
        else map.delete(id);
        return map;
      });

    for (const id of leavingNow) {
      const at = swiped.current.get(id);
      const hold = at == null ? 0 : Math.max(0, SWIPE_CONFIRM_MS + SWIPE_FADE_MS - (performance.now() - at));
      const token = ++departures.current;
      setDeparting((current) => new Map(current).set(id, { savedView, closing: hold === 0, token }));
      if (hold) window.setTimeout(() => update(id, token, { savedView, closing: true, token }), hold);
      window.setTimeout(() => update(id, token, null), hold + HIDE_MS);
    }
    swiped.current.clear();
    setListedMarked(markedIds);

    if (!savedView && removed.some((id) => !feed.items.some((e) => e.id === id))) setRefreshNonce((n) => n + 1);
    // Only when the lists change, not when a page loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markedIds]);

  /** Departures from the list on screen. One from the other list has no card here to play on. */
  const { kept: departingHere, closing: closingHere } = useMemo(() => {
    const kept = new Set<string>();
    const closing = new Set<string>();
    for (const [id, d] of departing) {
      if (d.savedView !== asked.savedOnly) continue;
      kept.add(id);
      if (d.closing) closing.add(id);
    }
    return { kept, closing };
  }, [departing, asked.savedOnly]);

  /**
   * What the list shows: the pages loaded, less anything that has changed since
   * they were asked for — an event that ended, one just marked Not Interested
   * or taken off both lists under the Saved pill — with the user's edited
   * events placed in order among them.
   */
  const visible = useMemo(() => {
    const listed = feed.items.filter(
      (e) =>
        !editedIds.has(e.id) &&
        (asked.showPast || !isPastEvent(e, now)) &&
        (asked.showHidden || !hidden.ids.has(e.id)) &&
        ((asked.savedOnly ? listedMarked.has(e.id) : !listedMarked.has(e.id)) || departingHere.has(e.id)),
    );
    if (editedMatches.length === 0) return listed;

    // Only inside the stretch of the week already loaded; beyond either end,
    // the next page brings the events around it and it lands then.
    const first = listed[0];
    const last = listed[listed.length - 1];
    const out = [...listed];
    for (const event of editedMatches) {
      if (feed.hasPrevious && (!first || compareOpportunities(event, first) < 0)) continue;
      if (feed.hasMore && (!last || compareOpportunities(event, last) > 0)) continue;
      const at = out.findIndex((e) => compareOpportunities(e, event) > 0);
      out.splice(at === -1 ? out.length : at, 0, event);
    }
    return out;
  }, [feed.items, feed.hasPrevious, feed.hasMore, editedIds, editedMatches, asked, hidden.ids, listedMarked, departingHere, now]);

  /** "N": every match, with the edited events the API was told to leave out. */
  const served = useOpportunityCount(request, tick);
  const total = served == null ? null : served + editedMatches.length;

  /** "M": the pool the filters run over. */
  const pool = useMemo(() => poolQuery(asked, context), [asked, context]);
  const poolServed = useOpportunityCount(pool, tick);
  const poolEdited = useMemo(
    () =>
      editedEvents.filter(
        (e) => (asked.showPast || !isPastEvent(e, now)) && (asked.showHidden || !hidden.ids.has(e.id)),
      ).length,
    [editedEvents, asked.showPast, asked.showHidden, hidden.ids, now],
  );
  const poolTotal = poolServed == null ? null : poolServed + poolEdited;

  /** Beside each toggle: how many events turning it on would add. */
  const hiddenQuery = useMemo(() => {
    const ids = [...hidden.ids].filter(isOpportunityId);
    return ids.length ? { id: ids, includePast: asked.showPast || undefined } : null;
  }, [hidden.ids, asked.showPast]);
  const hiddenCount = useOpportunityCount(hiddenQuery, tick);


  const shownCount = total ?? visible.length;
  const poolCount = poolTotal ?? shownCount;

  /**
   * "Nothing matches yet. Drop Fashion to see 41 events."
   *
   * Only once a result has come back empty: it costs a count per active
   * selection, and a list with results has no use for it.
   */
  const [suggestion, setSuggestion] = useState<{ key: string; value: LoosenSuggestion | null } | null>(null);
  const empty = feed.generation > 0 && !feed.stale && feed.status === 'ready' && visible.length === 0;
  useEffect(() => {
    if (!empty) return;
    const controller = new AbortController();
    findLoosenSuggestion(asked, context, vocabulary, editedEvents, now, controller.signal)
      .then((value) => setSuggestion({ key: filterKey, value }))
      .catch(() => {
        // No suggestion is the same as "try removing a filter".
      });
    return () => controller.abort();
    // Asked once per empty result, not again whenever the clock or a list moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empty, filterKey]);
  const shownSuggestion = suggestion?.key === filterKey ? suggestion.value : null;

  /**
   * New criteria, same place in the week.
   *
   * A filter is an adjustment to a search you are already reading, so throwing
   * the list back to the top costs more than the filter saves. The first page
   * was asked for at the card in the middle of the pane; when it lands, that
   * card is put back where it was — or, if the new filters exclude it, the
   * first event at or after that moment is centered.
   *
   * A layout effect, not an effect: the new list is about to make the browser
   * clamp the scroll position and fire a scroll event, and that event would
   * overwrite the very anchor being read here.
   */
  const restoreAnchor = listAnchor.restore;
  const clearAnchor = listAnchor.clear;
  const seenGeneration = useRef(feed.generation);
  useLayoutEffect(() => {
    if (seenGeneration.current === feed.generation) return;
    seenGeneration.current = feed.generation;
    restoreAnchor({ events: visible, at: feed.landing, anchorId: feed.anchorId });
  }, [feed.generation, feed.landing, feed.anchorId, visible, restoreAnchor]);

  /** The events pane feeds two readers: where you were, and what you were on. */
  const rememberScroll = listScroll.onScroll;
  const trackAnchor = listAnchor.onScroll;
  const onListScroll = useCallback(() => {
    rememberScroll();
    trackAnchor();
  }, [rememberScroll, trackAnchor]);

  // --- filter mutations ----------------------------------------------------

  const toggleIn = useCallback(
    <T extends string>(set: Set<T>, value: T): Set<T> => {
      const next = new Set(set);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    },
    [],
  );

  const setQuery = useCallback(
    (query: string) => setFilters((f) => ({ ...f, query })),
    [],
  );

  // Report searches once the user pauses, not on every keystroke.
  useEffect(() => {
    const q = searched.trim();
    if (!q || total == null) return;
    const id = setTimeout(() => {
      track({
        t: 'search',
        len: q.length,
        results: total,
        ...(total === 0 ? { query: q.slice(0, 64) } : {}),
      });
    }, 900);
    return () => clearTimeout(id);
  }, [searched, total]);

  const onToggleDay = useCallback(
    (key: string) =>
      setFilters((f) => {
        const days = toggleIn(f.days, key);
        track({ t: 'filter_change', facet: 'day', value: key, on: days.has(key), active: activeFilterCount({ ...f, days }), results: 0 });
        return { ...f, days };
      }),
    [toggleIn],
  );

  const onToggleSlot = useCallback(
    (slot: TimeSlot) =>
      setFilters((f) => {
        const slots = toggleIn(f.slots, slot);
        track({ t: 'filter_change', facet: 'time', value: slot, on: slots.has(slot), active: activeFilterCount({ ...f, slots }), results: 0 });
        return { ...f, slots };
      }),
    [toggleIn],
  );

  const onToggleGoal = useCallback(
    (key: string) => {
      setFilters((f) => {
        const goals = toggleIn(f.goals, key);
        // Reported by label, as it always was, so the numbers read on across the move.
        const label = vocabulary.goalLabel(key);
        track({ t: 'filter_change', facet: 'intent', value: label, on: goals.has(key), active: activeFilterCount({ ...f, goals }), results: 0 });

        if (goals.has(key)) {
          // log Analytics event when a filter is selected
          ReactGA.event('select_content', { content_type: 'intent_filter_selected', content_id: label });
        }

        return { ...f, goals };
      });
    },
    [toggleIn, vocabulary],
  );

  const onToggleTopic = useCallback(
    (key: string) =>
      setFilters((f) => {
        const topics = toggleIn(f.topics, key);
        const label = vocabulary.topicLabel(key);
        track({ t: 'filter_change', facet: 'topic', value: label, on: topics.has(key), active: activeFilterCount({ ...f, topics }), results: 0 });

        if (topics.has(key)) {
          // log Analytics event when a tag is selected
          ReactGA.event('select_content', { content_type: 'tag_filter_selected', content_id: label });
        }

        return { ...f, topics };
      }),
    [toggleIn, vocabulary],
  );

  const onToggleOther = useCallback(
    (id: OtherFilter) =>
      setFilters((f) => {
        let others = toggleIn(f.others, id);
        // In-person and Online are opposites; selecting one clears the other.
        if (others.has('inPerson') && others.has('online')) {
          others = new Set([...others].filter((x) => x === id || (x !== 'inPerson' && x !== 'online')));
        }
        track({ t: 'filter_change', facet: 'other', value: id, on: others.has(id), active: activeFilterCount({ ...f, others }), results: 0 });
        return { ...f, others };
      }),
    [toggleIn],
  );

  const onResetFacet = useCallback((facet: 'days' | 'slots' | 'goals' | 'topics' | 'others') => {
    setFilters((f) => ({ ...f, [facet]: new Set() }) as FilterState);
  }, []);

  /** The wordmark behaves like a home link: Explore, no filters, scrolled to top. */
  const resetToHome = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    closeDetail();
    setReporting(null);
    setEditing(null);
    setAboutOpen(false);
    setView('explore');
    // The wordmark means "start over": every pane back to the top, a fresh
    // signature so the list drops back to its first page, and no anchor — the
    // signature change would otherwise put them straight back where they were.
    clearScrollMemory();
    clearAnchor();
    setHomeNonce((n) => n + 1);
    track({ t: 'filter_change', facet: 'reset', value: 'home', on: false, active: 0, results: 0 });
  }, [clearAnchor, closeDetail]);

  const onResetAll = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    track({ t: 'filter_change', facet: 'reset', value: 'all', on: false, active: 0, results: 0 });
  }, []);

  const onLoosen = useCallback((s: LoosenSuggestion) => {
    setFilters((f) => relax(f, s.facet, s.value));
  }, []);

  // --- event actions -------------------------------------------------------

  const openDetail = useCallback((event: Opportunity, rank: number) => {
    showDetail(event);
    setDetailFromDay(false);
    track({ t: 'event_open', id: event.id, from: 'card', rank });

    // log Analytics event when an event is clicked on
    ReactGA.event('event_details_opened', { event: event.name, rank });
  }, [showDetail]);

  /**
   * Wrappers that mutate a list and then say what happened. The message has to
   * be composed before the toggle runs, since afterwards the membership has
   * already flipped.
   */
  const scheduleWithToast = useCallback(
    (id: string, from: 'card' | 'detail' | 'schedule') => {
      const event = byId.get(id);
      const adding = !collections.isScheduled(id);
      collections.toggleScheduled(id, from, event?.name);
      if (!event) return;
      if (adding) {
        toasts.show(quoted(event.name, 'added to My Schedule.'), {
          kind: 'goToSchedule',
          eventId: id,
          dayKey: localDate(event),
        });
      } else {
        toasts.show(quoted(event.name, 'removed from My Schedule.'), { kind: 'undo' });
      }

      // log Analytics event when an event is added to/removed from a user's schedule
      ReactGA.event('add_to_schedule', { event: event.name, action: adding ? 'added' : 'removed', from });
    },
    [byId, collections, toasts],
  );

  const saveWithToast = useCallback(
    (id: string, from: 'card' | 'detail' | 'schedule') => {
      const event = byId.get(id);
      const adding = !collections.isSaved(id);
      collections.toggleSaved(id, from, event?.name);
      if (!event) return;
      toasts.show(
        adding
          ? quoted(event.name, 'saved for later.')
          : quoted(event.name, 'removed from Save for Later.'),
        adding ? undefined : { kind: 'undo' },
      );

      // log Analytics event when an event is saved/unsaved for later
      ReactGA.event('save_for_later', { event: event?.name, action: adding ? 'saved' : 'unsaved', from });
    },
    [byId, collections, toasts],
  );

  /**
   * Moves an event between the two lists from the day view.
   *
   * The lists are mutually exclusive, so each toggle does both halves on its
   * own: scheduling something already saved un-saves it, and vice versa.
   */
  const toggleListMembership = useCallback(
    (id: string) => {
      if (collections.isSaved(id)) scheduleWithToast(id, 'schedule');
      else saveWithToast(id, 'schedule');
    },
    [collections, scheduleWithToast, saveWithToast],
  );

  /** The ✕ on a schedule card drops it from whichever list holds it. */
  const removeFromSchedule = useCallback(
    (id: string) => {
      const title = byId.get(id)?.name ?? 'This event';
      if (collections.isScheduled(id)) collections.toggleScheduled(id, 'schedule', title);
      else if (collections.isSaved(id)) collections.toggleSaved(id, 'schedule', title);
      else return;
      toasts.show(quoted(title, 'removed from My Schedule.'), { kind: 'undo' });

      // log Analytics event when an event is removed from a user's schedule
      ReactGA.event('add_to_schedule', { event: title, action: 'removed' });
    },
    [collections, byId, toasts],
  );

  // --- Not Interested ------------------------------------------------------

  /** Cards playing their hide animation, and the timers that finish it. */
  const [leaving, setLeaving] = useState<Set<string>>(() => new Set());
  const leaveTimers = useRef(new Map<string, number>());

  const stopLeaving = useCallback((id: string) => {
    setLeaving((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const unhideEvent = useCallback(
    (id: string) => {
      const timer = leaveTimers.current.get(id);
      if (timer != null) {
        window.clearTimeout(timer);
        leaveTimers.current.delete(id);
        stopLeaving(id);
      }
      hidden.unhide(id);
      // Hidden before these pages were asked for, so they never held it.
      if (!filters.showHidden && !feed.items.some((e) => e.id === id)) setRefreshNonce((n) => n + 1);
    },
    [hidden, stopLeaving, filters.showHidden, feed.items],
  );

  /**
   * "Not Interested". The card fades and the cards below close the gap, and only
   * then does the event leave the list, so the animation has a card to play on.
   * Refused for events on either list; the controls explain why.
   */
  const hideEvent = useCallback(
    (id: string) => {
      const event = publishedById.get(id);
      if (!event || collections.isScheduled(id) || collections.isSaved(id)) return;
      toasts.show('Event has been hidden from your Explore page.', { kind: 'unhide', eventId: id });
      ReactGA.event('not_interested', { event: event.name, action: 'hidden' });

      // With hidden events shown, the card stays and just changes state.
      if (filters.showHidden) {
        hidden.hide(id);
        return;
      }
      setLeaving((current) => new Set(current).add(id));
      leaveTimers.current.set(
        id,
        window.setTimeout(() => {
          leaveTimers.current.delete(id);
          hidden.hide(id);
          stopLeaving(id);
        }, HIDE_MS),
      );
    },
    [publishedById, collections, toasts, filters.showHidden, hidden, stopLeaving],
  );

  const toggleHidden = useCallback(
    (id: string) => {
      if (!hidden.isHidden(id)) {
        hideEvent(id);
        return;
      }
      unhideEvent(id);
      toasts.show('Event is back on your Explore page.');
      ReactGA.event('not_interested', { event: publishedById.get(id)?.name, action: 'unhidden' });
    },
    [hidden, hideEvent, unhideEvent, toasts, publishedById],
  );

  // --- Edit this event -----------------------------------------------------

  /** Every event-type word, for the edit form's drop-down, alphabetical as it has always been. */
  const formats = useMemo(() => [...vocabulary.eventTypes].sort(), [vocabulary.eventTypes]);

  const saveEdit = useCallback(
    (fields: EventFields, scope: EditScope) => {
      const original = editing ? directory.byId.get(editing.id) : undefined;
      setEditing(null);
      if (!original) return;

      const stored = edits.save(original, fields, scope);
      const updated = stored ? applyEdit(original, stored) : original;
      updateDetail(updated);

      if (scope === 'me') {
        // The toast says My Schedule, so it has to be there, and a scheduled
        // event can't also be hidden.
        if (hidden.isHidden(original.id)) unhideEvent(original.id);
        if (!collections.isScheduled(original.id)) {
          collections.toggleScheduled(original.id, 'detail', updated.name);
        }
        toasts.show('Changes saved to My Schedule.');
      } else {
        toasts.show('Changes submitted for review.');
      }
      ReactGA.event('event_edit', { event: original.name, scope });
    },
    [editing, directory.byId, edits, hidden, unhideEvent, collections, toasts, updateDetail],
  );

  const revertEdit = useCallback(
    (id: string) => {
      edits.revert(id);
      const original = directory.byId.get(id);
      if (original) updateDetail(original);
      // Edited before these pages were asked for, so they never held the listing.
      if (!feed.items.some((e) => e.id === id)) setRefreshNonce((n) => n + 1);
      toasts.show('Your edits to this event were removed.');
    },
    [edits, directory.byId, feed.items, toasts, updateDetail],
  );

  const scheduledEvents = useMemo(
    () => {
      const fromLists = [...collections.scheduled]
        .map((id) => byId.get(id))
        .filter((e): e is Opportunity => Boolean(e));
      // Personal events are not in `collections`: they are on the schedule by
      // existing, so nothing can remove one without deleting it.
      return [...fromLists, ...personal.events].sort(byWhen);
    },
    [collections.scheduled, byId, personal.events],
  );
  /** An event opened from the right sidebar's day: the user's own schedule. */
  const openFromSchedule = useCallback(
    (event: Opportunity) => {
      showDetail(event);
      setDetailFromDay(false);
      track({ t: 'event_open', id: event.id, from: 'schedule', rank: -1 });
    },
    [showDetail],
  );
  const savedEvents = useMemo(
    () => [...collections.saved].map((id) => byId.get(id)).filter((e): e is Opportunity => Boolean(e)),
    [collections.saved, byId],
  );

  /**
   * The week's four numbers and the one way to clear it, for whatever App
   * draws over itself. The account page shows the same block on a phone, where
   * My Schedule has no sidebar to show it in (`shared/weekContext.ts`).
   */
  const weekTotals = useWeekStats(scheduledEvents, savedEvents, attendance.trims);
  const week = useMemo<WeekSummary>(
    () => ({
      stats: weekTotals,
      counts: {
        all: scheduledEvents.length + savedEvents.length,
        scheduled: scheduledEvents.length,
      },
      clear: collections.clear,
    }),
    [weekTotals, scheduledEvents.length, savedEvents.length, collections.clear],
  );

  /** My Schedule's own sheet, so a view change can run its cancel path. */
  const schedule = useRef<ScheduleHandle | null>(null);

  /**
   * Everything opened over one page, closed. Below 900px a sheet belongs to the
   * page it was opened from, and the toggle is now reachable while one is up:
   * nothing opened on one page may be left floating over another.
   */
  const closeSheets = useCallback(() => {
    closeDetail();
    setFiltersOpen(false);
    schedule.current?.cancelFormSheet();
  }, [closeDetail]);

  const changeView = useCallback(
    (v: ViewName) => {
      setView(v);
      // The filters belong to Explore, and travel off screen with it.
      if (v !== 'explore') setFiltersOpen(false);
      if (isNarrow) closeSheets();
      track({ t: 'view_change', to: v });
    },
    [isNarrow, closeSheets],
  );

  /**
   * Account (D3): the Account page when logged in, AuthKit's login (where
   * sign-up lives) when not, coming back to this view. The page is drawn over
   * the app, so anything the app has open over itself goes first.
   */
  const openAccount = useCallback(() => {
    closeSheets();
    toasts.dismiss();
    // While AuthKit is still loading, the Account page waits to find out.
    if (user || authIsLoading) {
      navigate(ACCOUNT_ROOT);
      return;
    }
    const returnTo: ReturnTo = { path: '/', arrival: view === 'schedule' ? { view: 'schedule' } : undefined };
    navigate(ACCOUNT_PATHS.logIn, { state: { returnTo } });
  }, [closeSheets, toasts, user, authIsLoading, navigate, view]);

  /**
   * The view toggle, and the phone header's two contents, which move together.
   * They are painted rather than rendered: a tap has to move the pill on the
   * next frame, and the view it switches to can take most of a second to render
   * behind it.
   */
  const toggleRef = useRef<ViewToggleHandle | null>(null);
  const headerFade = useRef<CrossfadeHandle | null>(null);
  /** Set while a tap's own move plays; the page swipe's offsets are ignored until it lands. */
  const toggleTap = useRef(false);
  const tapTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(tapTimer.current), []);

  const paintChrome = useCallback((at: number, ms: number, ease: string) => {
    toggleRef.current?.follow(at, ms, ease);
    // The header only ever holds the two views; Account is drawn over it.
    if (at <= 1) headerFade.current?.follow(at, ms, ease);
  }, []);

  /**
   * A tap on the toggle. The pill commits and paints first, and the view
   * follows two frames later, so the slide is already running by the time React
   * renders the page behind it.
   */
  const tapToggle = useCallback(
    (item: ToggleItem) => {
      if (item === 'account') {
        openAccount();
        return;
      }
      if (accountPage) {
        // Back to the app, on whichever view was tapped. The pill animates out
        // of its circle from its own value change.
        if (item !== view) changeView(item);
        navigate('/');
        return;
      }
      if (item === view) return;
      toggleTap.current = true;
      window.clearTimeout(tapTimer.current);
      tapTimer.current = window.setTimeout(() => {
        toggleTap.current = false;
      }, TOGGLE_MOVE_MS);
      paintChrome(item === 'schedule' ? 1 : 0, TOGGLE_MOVE_MS, TOGGLE_EASE);
      requestAnimationFrame(() => requestAnimationFrame(() => changeView(item)));
    },
    [openAccount, accountPage, view, changeView, navigate, paintChrome],
  );

  /**
   * Back from logging in, or from the account page's own history. App is no
   * longer rebuilt by that trip, so the arrival has to be answered here rather
   * than by the view's initial state alone.
   */
  useEffect(() => {
    if (arrivalFrom(location.state)?.view === 'schedule') setView('schedule');
  }, [location.state]);

  /** A mini day clicked, off its events: that day on My Schedule, with the same hours in view. */
  const openDayAt = useCallback(
    (dayKey: string, minute: number) => {
      closeDetail();
      setPendingDay({ dayKey, minute });
      changeView('schedule');
    },
    [closeDetail, changeView],
  );

  /** The first page, and the events on the user's own lists, before anything is drawn. */
  const firstLoad = feed.generation === 0;
  const loading = (firstLoad && feed.status !== 'error') || !directory.ready;
  const error = firstLoad && feed.status === 'error' ? feed.error : null;

  /**
   * A sheet half up over the day view: the callout, or the phone's personal
   * event form. The header slides away while one is, as My Schedule's own row of
   * buttons does, so the day has the screen above it.
   */
  const [formSheetOpen, setFormSheetOpen] = useState(false);
  const calloutPeeking =
    view === 'schedule' && ((Boolean(detail) && calloutLayout === 'half') || formSheetOpen);
  useLayoutEffect(() => {
    const node = header.current;
    if (!node) return;
    node.style.transition = 'margin-top 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease';
    node.style.marginTop = calloutPeeking ? `-${node.offsetHeight}px` : '';
    node.style.opacity = calloutPeeking ? '0' : '';
    node.style.pointerEvents = calloutPeeking ? 'none' : '';
  }, [calloutPeeking]);

  // --- below 900px: the filter panel, and the two pages side by side -------

  const pages = useRef<PageSwipeHandle | null>(null);
  const filterPanel = useRef<FilterPanelHandle | null>(null);

  /**
   * My Schedule is built beside Explore the first time it's needed, or shortly
   * after arrival, so that the first swipe toward it isn't also its first render.
   */
  const [schedulePreloaded, setSchedulePreloaded] = useState(false);
  const scheduleReady = schedulePreloaded || view === 'schedule';
  useEffect(() => {
    if (!isNarrow || loading || schedulePreloaded) return;
    const timer = window.setTimeout(() => setSchedulePreloaded(true), 1500);
    return () => window.clearTimeout(timer);
  }, [isNarrow, loading, schedulePreloaded]);

  // Wide again: the sidebar is back, and the panel's state goes with the panel.
  useEffect(() => {
    if (!isNarrow) setFiltersOpen(false);
  }, [isNarrow]);

  /** Swipes are for fingers: on a phone, the emulator's mouse counts as one. */
  const swipeInput = useCallback(
    (e: React.PointerEvent) => e.pointerType !== 'mouse' || isMobile,
    [isMobile],
  );

  /**
   * Explore's own swipes, anywhere but on an event card: right pulls the filters
   * open, left moves to My Schedule. One gesture, handed to whichever of the two
   * its direction asks for.
   */
  const exploreSwipeTo = useRef<'filters' | 'schedule'>('filters');
  const onExploreSwipe = useSwipeGesture({
    accepts: (e) => view === 'explore' && !filtersOpen && swipeInput(e) && !startsOnOwnDrag(e.target),
    claim: (direction) => {
      exploreSwipeTo.current = direction === 1 ? 'filters' : 'schedule';
      if (direction === -1) setSchedulePreloaded(true);
      return true;
    },
    move: (dx) => {
      if (exploreSwipeTo.current === 'filters') filterPanel.current?.dragOpen(dx);
      else pages.current?.drag(dx);
    },
    release: (dx, velocity) => {
      if (exploreSwipeTo.current === 'filters') filterPanel.current?.releaseOpen(dx, velocity);
      else pages.current?.release(dx, velocity);
    },
    cancel: () => {
      if (exploreSwipeTo.current === 'filters') filterPanel.current?.cancelOpen();
      else pages.current?.cancel();
    },
  });

  /**
   * Explore's offset as the pages move: the filter tab travels with it, and so
   * do the toggle's highlight and the header's two contents. A tap drives them
   * itself, and while it does, PageSwipe's own offsets are ignored — its
   * layout effect repaints from the page that was showing, which would snap the
   * highlight back a box mid-move.
   */
  const followExplore = useCallback(
    (px: number, ms: number, ease: string) => {
      filterPanel.current?.follow(px, ms, ease);
      if (toggleTap.current) return;
      paintChrome(Math.max(0, Math.min(1, -px / (window.innerWidth || 1))), ms, ease);
    },
    [paintChrome],
  );

  /**
   * The card swipe, performed once on the reader's behalf: on a phone, the first
   * time the events are on screen with nothing in front of them — after the
   * welcome, with the filters closed. Once per browser: it is a lesson, and a
   * lesson repeated is an animation in the way.
   */
  useEffect(() => {
    if (!isMobile || loading || error || view !== 'explore' || mobileNotice.open || filtersOpen) return;
    if (hinted.current) return;
    const timer = window.setTimeout(() => {
      hinted.current = true;
      try {
        if (localStorage.getItem(SWIPE_HINT_KEY)) return;
        localStorage.setItem(SWIPE_HINT_KEY, '1');
      } catch {
        // Site data blocked: show it this session and move on.
      }
      setSwipeHint((n) => n + 1);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [isMobile, loading, error, view, mobileNotice.open, filtersOpen]);

  // --- render --------------------------------------------------------------

  const sidebar = (
    <Sidebar
      filters={filters}
      topics={vocabulary.topics}
      goals={vocabulary.goals}
      onQuery={setQuery}
      onToggleDay={onToggleDay}
      onToggleSlot={onToggleSlot}
      onToggleGoal={onToggleGoal}
      onToggleTopic={onToggleTopic}
      onToggleOther={onToggleOther}
      onResetFacet={onResetFacet}
      onResetAll={onResetAll}
      hiddenCount={hiddenCount}
      onToggleShowHidden={() => setFilters((f) => ({ ...f, showHidden: !f.showHidden }))}
    />
  );

  /** From 900px up the lockup goes wherever there is room: the left sidebar, or beside the toggle. */
  const lockupIn = (size: string) => (
    <Lockup variant="wide" size={size} onHome={resetToHome} onAbout={() => setAboutOpen(true)} />
  );

  /** Explore, My Schedule and Account — or Sign In — in one control. */
  const toggleValue: ToggleValue = accountPage ? 'account' : view;
  /** Drawn on the app and on the account page itself, and on nothing else. */
  const toggleShown = location.pathname === '/' || accountPage;
  const viewToggle = (fill: 'phone' | 'desktop', scale = 1) => (
    <ViewToggle
      ref={toggleRef}
      value={toggleValue}
      pill={togglePill}
      fill={fill}
      scale={scale}
      onSelect={tapToggle}
      onSignIn={openAccount}
    />
  );

  /** From 900px up: the right sidebar's top, or the right of the top strip when it is closed. */
  const siteLinks = (
    <>
      <TextLink onClick={() => setAboutOpen(true)}>About Us</TextLink>
      <TextLink onClick={() => window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer')}>
        Submit Feedback!
      </TextLink>
    </>
  );

  // Replaced by the one Account link below (D3): sign-up lives on AuthKit's
  // login page, and Log out on the Account page.
  // /**
  //  * Preview only: accounts are not live, and the pages say so. Always in the
  //  * top strip, so the right sidebar's two links keep the spacing they have in
  //  * production.
  //  */
  // const accountLinks = window.location.hostname === 'localhost' && (
  //   <PreviewLinks>
  //     {!user ? (
  //       <>
  //         <TextLink onClick={() => navigate(ACCOUNT_PATHS.logIn)}>Log in</TextLink>
  //         <TextLink onClick={() => navigate(ACCOUNT_PATHS.signUp)}>Sign up</TextLink>
  //       </>
  //     ) : (
  //       <>
  //         <TextLink onClick={() => {}}>{user.firstName ?? user.email}</TextLink>
  //         <TextLink onClick={() => signOut()}>Log out</TextLink>
  //       </>
  //     )}
  //   </PreviewLinks>
  // );

  // The top strip's "Account" text link, and the filter panel's, are gone: the
  // view toggle's third item is the way in on every layout now (Sam, Sept 19).
  // `accountShown` and `openAccount` are defined with the toggle, above.

  const savedPill = (phone: boolean) => (
    <SavedPill
      $on={filters.savedOnly}
      $phone={phone}
      aria-pressed={filters.savedOnly}
      onClick={() => setFilters((f) => ({ ...f, savedOnly: !f.savedOnly }))}
    >
      <Icon name={filters.savedOnly ? 'ui:saveOn' : 'ui:save'} size={phone ? 12 : 13} />
      {filters.savedOnly ? (
        <Icon name="ui:scheduleOn" size={phone ? 12.3 : 14} />
      ) : (
        <OutlinedCircle mark="check" size={phone ? 12.3 : 14} />
      )}
      <Box component="span" sx={{ ml: phone ? '3px' : 0.75 }}>Saved</Box>
      <Box component="span" sx={{ ml: phone ? '3.8px' : 0, opacity: filters.savedOnly ? 1 : 0.6 }}>
        {markedIds.size}
      </Box>
    </SavedPill>
  );

  /**
   * Explore's half of a phone's header, where the toggle was: Saved, with the
   * count hanging under it. Out of the list's sticky results bar, which a phone
   * no longer draws.
   */
  const phoneExploreChrome = (
    <>
      {savedPill(true)}
      <ShowingLine>
        Showing {shownCount} of {poolCount} events
      </ShowingLine>
    </>
  );

  const eventsColumn = (
      <PaneWrap>
      <Pane ref={listScroll.ref} onScroll={onListScroll}>
        <Centered>
        {/* A phone's Saved and count have moved into the header. Every other
            layout keeps the bar in the list, where it has always been; a
            desktop window under 600px stacks its two items at the right. */}
        {!phoneChrome && (
          <ResultsBar>
            {/* The phone says the count in words and leaves the heading to
                the filter panel, which is already titled. */}
            {isMobile ? (
              <Typography sx={{ fontSize: 13, color: 'rgba(243,238,231,0.6)' }}>
                Showing {shownCount} of {poolCount} events
              </Typography>
            ) : (
              <Typography
                sx={{ fontFamily: FONTS.display, fontSize: 19, fontWeight: 700 }}
              >
                {activeFilterCount(filters) > 0 ? 'Search Results' : 'All Events'}
                <Box
                  component="span"
                  sx={{ fontSize: 12.5, opacity: 0.6, ml: 1.2, fontWeight: 600 }}
                >
                  {shownCount} of {poolCount}
                </Box>
              </Typography>
            )}

            {savedPill(false)}
          </ResultsBar>
        )}

        <EventList
          events={visible}
          total={total}
          ready={feed.generation > 0}
          generation={feed.generation}
          hasMore={feed.hasMore}
          hasPrevious={feed.hasPrevious}
          loadMore={feed.loadMore}
          loadPrevious={feed.loadPrevious}
          moreError={feed.moreError || feed.status === 'error'}
          onRetry={feed.retry}
          pane={listScroll.node}
          now={now}
          suggestion={shownSuggestion}
          isScheduled={collections.isScheduled}
          isSaved={collections.isSaved}
          savedView={asked.savedOnly}
          onOpen={openDetail}
          // A phone card changes lists only by a swipe, which confirms in
          // place before its row closes.
          onToggleSchedule={(id) => {
            if (isMobile) swiped.current.set(id, performance.now());
            scheduleWithToast(id, 'card');
          }}
          onToggleSave={(id) => {
            if (isMobile) swiped.current.set(id, performance.now());
            saveWithToast(id, 'card');
          }}
          onReport={setReporting}
          onFilterTopic={onToggleTopic}
          isHidden={hidden.isHidden}
          leavingIds={closingHere.size ? new Set([...leaving, ...closingHere]) : leaving}
          onToggleHidden={toggleHidden}
          onLoosen={onLoosen}
          onResetAll={onResetAll}
          swipeHint={swipeHint}
          onFocusDay={isNarrow ? undefined : setFocusedDay}
          onHover={isNarrow ? undefined : hover.set}
        />
        </Centered>
      </Pane>
      <Fade $edge="top" $phone={phoneChrome} />
      <Fade $edge="bottom" $phone={phoneChrome} />
      {!isNarrow && (
        <>
          <ToggleSpot $side="left">
            <SidebarToggle side="left" open={filtersShown} what="filters" onToggle={toggleLeft} />
          </ToggleSpot>
          <ToggleSpot $side="right">
            <SidebarToggle side="right" open={rightHolds !== null} what="panel" onToggle={toggleRight} />
          </ToggleSpot>
        </>
      )}
      </PaneWrap>
  );

  /**
   * The callout, drawn once: in the right sidebar of either view where it is
   * one, and otherwise over the page.
   */
  const callout = (
    <EventDetailSheet
      event={detail}
      layout={calloutLayout}
      past={detail ? isPastEvent(detail) : false}
      trims={attendance.trims}
      legChoices={legChoices.choices}
      onTrim={attendance.setEdge}
      onResetTrim={attendance.reset}
      scheduled={detail ? collections.isScheduled(detail.id) : false}
      saved={detail ? collections.isSaved(detail.id) : false}
      scheduledEvents={scheduledEvents}
      onClose={closeDetail}
      onBack={detailBack}
      onForward={detailForward}
      onToggleSchedule={(id) => scheduleWithToast(id, 'detail')}
      onToggleSave={(id) => saveWithToast(id, 'detail')}
      onReport={(e) => setReporting(e)}
      hidden={detail ? hidden.isHidden(detail.id) : false}
      edited={detail ? (edits.edits[detail.id]?.scope ?? null) : null}
      onToggleHidden={(id) => {
        // Hiding closes the callout, so the card can be seen leaving.
        if (!hidden.isHidden(id)) closeDetail();
        toggleHidden(id);
      }}
      onEdit={setEditing}
      onRevertEdit={revertEdit}
      onOpenEvent={(e) => {
        showDetail(e);
        // -1: the conflict list is not a ranked result set.
        track({ t: 'event_open', id: e.id, from: 'detail', rank: -1 });
      }}
      home={home.home}
      onOpenDay={openDayAt}
      onRemoveFromSchedule={removeFromSchedule}
      onGoToSchedule={() => {
        if (detail) setPendingReveal({ id: detail.id, dayKey: localDate(detail) });
        closeDetail();
        changeView('schedule');
      }}
    />
  );

  /**
   * With the callout beside the list, a click on the list's own ground — not a
   * card, not a control — closes it and puts the right sidebar back. The list
   * stays live for cards; its blank space is the way out.
   */
  const onListGround = (e: React.MouseEvent) => {
    if (rightHolds !== 'callout') return;
    if ((e.target as Element).closest('article, button, a, input, textarea, select, label, [role="button"], [role="radio"], [role="switch"]')) return;
    if (window.getSelection()?.toString()) return;
    closeDetail();
  };

  const explorePage = (
    <Panes>
      {/* The fades live inside PaneWrap, so they are scoped to the events
          column and cannot reach the sidebar in the first place. */}
      {/* Drawn whenever the page is wide enough, and collapsed when stepped
          aside or closed, so it eases away rather than vanishing. */}
      {!isNarrow && (
        <ResizableSidebar
          scrollKey="explore:filters"
          flat
          collapsed={!filtersShown}
          fraction={leftFraction}
          maxFraction={maxLeftFraction}
          onResize={setLeftShare}
          onReset={() => setLeftShare(null)}
          top={lockupIn(LOCKUP_IN_SIDEBAR)}
        >
          {sidebar}
        </ResizableSidebar>
      )}

      {isNarrow ? (
        eventsColumn
      ) : (
        <CentreColumn onClick={onListGround}>
          <TopStrip style={{ '--strip-left': `${filtersEdge}px` } as CSSProperties}>
            <div>{!filtersShown && <StripLockup>{lockupIn(LOCKUP_IN_STRIP)}</StripLockup>}</div>
            <StripLinks>
              {/* Not while the callout is the right sidebar: it has no row for
                  them, and there is no room here (Sam, Sept 18). */}
              {!rightHolds && siteLinks}
            </StripLinks>
          </TopStrip>
          {eventsColumn}
        </CentreColumn>
      )}

      {!isNarrow && (
        <RightColumn
          holds={rightHolds}
          panel={
            rightOpen && (
              <RightSidebar>
                <SidebarTop style={{ padding: `0 ${RIGHT_SIDEBAR_X}px` }}>{siteLinks}</SidebarTop>
                <RightSidebarBody>
                  {focusedDay && (
                    <DayFocus
                      dayKey={focusedDay}
                      scheduled={scheduledEvents}
                      hover={hover}
                      trims={attendance.trims}
                      legChoices={legChoices.choices}
                      home={home}
                      onOpenEvent={openFromSchedule}
                      onOpenDay={openDayAt}
                      onRemove={removeFromSchedule}
                    />
                  )}
                </RightSidebarBody>
              </RightSidebar>
            )
          }
          callout={calloutLayout === 'pane' ? callout : null}
        />
      )}
    </Panes>
  );

  const schedulePage = (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      <Schedule
        ref={schedule}
        scheduled={scheduledEvents}
        saved={savedEvents}
        savedIds={collections.saved}
        onRemove={removeFromSchedule}
        onToggleList={toggleListMembership}
        onClear={collections.clear}
        chrome={isNarrow ? undefined : { lockup: lockupIn(LOCKUP_IN_STRIP), links: siteLinks }}
        phoneChrome={phoneChrome}
        shareSlot={phoneChrome ? shareSlot : null}
        pendingReveal={pendingReveal}
        onRevealDone={() => setPendingReveal(null)}
        pendingDay={pendingDay}
        onPendingDayDone={() => setPendingDay(null)}
        onOpenEvent={(event, from) => {
          showDetail(event);
          setDetailFromDay(from === 'day');
        }}
        home={home}
        callout={calloutLayout === 'pane' ? callout : undefined}
        calloutOpen={rightHolds === 'callout'}
        onCloseCallout={closeDetail}
        calloutPeek={calloutPeeking}
        onFormSheet={setFormSheetOpen}
        attendance={attendance}
        legChoices={legChoices}
        personalDrafts={personal.drafts}
        onSavePersonal={(draft) => {
          const existing = personal.find(draft.id);
          personal.save(draft);
          toasts.show(
            existing
              ? quoted(draft.title.trim(), 'updated.')
              : quoted(draft.title.trim(), 'added to My Schedule.'),
          );
        }}
        onRetimePersonal={(id, startMinutes, endMinutes) => {
          const found = personal.find(id);
          if (!found) return;
          // No toast: this fires on every step of a drag.
          personal.save({ ...found, startMinutes, endMinutes });
        }}
        onDeletePersonal={(id) => {
          const title = personal.find(id)?.title.trim() || 'That event';
          personal.remove(id);
          toasts.show(quoted(title, 'deleted.'));
        }}
      />
    </Box>
  );

  /** Under the account page, nothing but the toggle answers a pointer or a reader. */
  const shut = accountOpen ? ({ inert: true, 'aria-hidden': true } as const) : null;

  return (
    <WeekContext.Provider value={week}>
    <Shell
      style={
        {
          '--toggle-x': `${toggleX}px`,
          '--toggle-w': `${toggleW}px`,
          // Only a phone has a band; everywhere else the fallbacks stand.
          ...(phoneChrome ? { '--toggle-band': TOGGLE_BAND, '--date-head-top': '0px' } : null),
        } as CSSProperties
      }
    >
      {/* From 900px up: one toggle for both views, drawn over the page, so a
          switch never moves it out from under the pointer. It is raised over
          the account page only while that is open; on `/` the callout drawer is
          still allowed to cover it. */}
      {!phoneChrome && !isNarrow && toggleShown && (
        <ToggleDock $over={accountOpen} style={{ left: toggleX }}>
          {viewToggle('desktop')}
        </ToggleDock>
      )}

      {/* A phone: pinned to the bottom of the screen, outside PageSwipe (whose
          off-screen page is inert and whose strip is transformed while moving)
          and outside Header (which slides away under a half-up sheet). */}
      {phoneChrome && toggleShown && <PhoneToggleDock>{viewToggle('phone')}</PhoneToggleDock>}

      {/* Below 900px only. From there up each column carries its own top. */}
      {isNarrow && (
        <Header
          ref={header}
          $template={phoneChrome ? '1fr auto' : 'auto 1fr'}
          $below={phoneChrome ? PHONE_HEADER_BELOW : 0}
        >
          <Box {...shut} sx={{ display: 'flex', minWidth: 0 }}>
            <Lockup
              variant={isMobile ? 'phone' : 'narrow'}
              size={`${isMobile ? WORDMARK_W.phone : WORDMARK_W.narrow}px`}
              onHome={resetToHome}
              onAbout={() => setAboutOpen(true)}
            />
          </Box>

          {phoneChrome ? (
            /* Where the toggle was: Explore's Saved and count, My Schedule's
               Share, cross-fading with the page swipe. */
            <HeaderRight {...shut}>
              <ViewCrossfade
                ref={headerFade}
                at={view === 'schedule' ? 1 : 0}
                first={phoneExploreChrome}
                second={<div ref={setShareSlot} style={{ display: 'flex' }} />}
              />
            </HeaderRight>
          ) : (
            /* A desktop window under 900px: the toggle keeps the top, centred
               between the lockup and the screen's right edge. It stays live
               over the account page, which is why it is not inside the cell
               above. */
            <HeaderToggle style={accountOpen ? { position: 'relative', zIndex: 1270 } : undefined}>
              {toggleShown && viewToggle('desktop', narrowToggle ? TOGGLE_NARROW_SCALE : 1)}
            </HeaderToggle>
          )}
        </Header>
      )}

      <Content {...shut}>
      {!isNarrow && (loading || error) && (
        <TopStrip>
          <div>{lockupIn(LOCKUP_IN_STRIP)}</div>
          <StripLinks>{siteLinks}</StripLinks>
        </TopStrip>
      )}

      {(loading || authIsLoading) && (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress sx={{ color: BRAND.parchment }} />
        </Box>
      )}

      {error && !loading && (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 3, textAlign: 'center' }}>
          <Box>
            <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, fontWeight: 700, mb: 1 }}>
              We couldn't load the events
            </Typography>
            <Typography sx={{ opacity: 0.7, fontSize: 14, mb: 2 }}>
              {error}. Check your connection and reload.
            </Typography>
            <Button
              onClick={() => window.location.reload()}
              sx={{ background: BRAND.parchment, color: BRAND.mocha, borderRadius: 999, px: 3, fontWeight: 700 }}
            >
              Reload
            </Button>
          </Box>
        </Box>
      )}

      {!loading && !error && isNarrow && (
        <>
          <PageSwipe
            ref={pages}
            view={view}
            onView={changeView}
            explore={explorePage}
            schedule={scheduleReady ? schedulePage : null}
            onExplorePointerDown={onExploreSwipe}
            accepts={swipeInput}
            onExploreOffset={followExplore}
          />
          <FilterPanel
            ref={filterPanel}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            available={view === 'explore'}
            shown={shownCount}
            total={poolCount}
            swipes
            mouseSwipes={isMobile}
          >
            {sidebar}
            {/* The phone header has no room for a third link; this is the
                preview's way in until accounts earn a place of their own. */}
            {/* <TextLink
              onClick={() => navigate(ACCOUNT_PATHS.logIn)}
              style={{ display: 'block', margin: '18px auto 0' }}
            >
              Log in
            </TextLink> */}
            {/* Account is the view toggle's third item now, on every layout. */}
            <TextLink
              onClick={() => window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer')}
              style={{ display: 'block', margin: '14px auto 0' }}
            >
              Submit Feedback!
            </TextLink>
          </FilterPanel>
        </>
      )}

      {!loading && !error && !isNarrow && view === 'explore' && explorePage}
      {!loading && !error && !isNarrow && view === 'schedule' && schedulePage}

      {calloutLayout !== 'pane' && callout}

      <ToastBar
        toast={toasts.toast}
        onUndo={collections.undo}
        onUnhide={(id) => {
          toasts.dismiss();
          unhideEvent(id);
        }}
        onGoToSchedule={(id, dayKey) => {
          toasts.dismiss();
          setPendingReveal({ id, dayKey });
          changeView('schedule');
        }}
      />

      <ReportDialog event={reporting} onClose={() => setReporting(null)} />
      <EventEditDialog
        event={editing}
        formats={formats}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
      />

      <MobileNotice open={mobileNotice.open} onDismiss={mobileNotice.dismiss} />
      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
      </Content>

      {/* The account pages, drawn over all of this and outside `Content`, which
          is inert while one is open (AppShell). */}
      {children}
    </Shell>
    </WeekContext.Provider>
  );
}
